import { useCallback, useSyncExternalStore } from "react";
import { deleteRulebookDraft, getRulebookDraft, getRulebookDraftOwner, subscribeRulebookDrafts } from "./rulebookDraftStore";
import { apiRulebookPublishingClient } from "./rulebookPublishingClient";
/**
 * What deleting a Editor draft means. An unpublished draft is deleted locally. A published one can't be: dropping it
 * would strand its server Rulebook with no way back into the Editor, so it is deleted as a Rulebook instead, on the
 * server (which detaches it from characters and personas), and only then locally.
 */
export default function useRulebookDeletion({ draftId, client = apiRulebookPublishingClient }) {
    const getLinkedId = useCallback(() => getRulebookDraft({ id: draftId })?.rulebook_id, [draftId]);
    const rulebookId = useSyncExternalStore(subscribeRulebookDrafts, getLinkedId, getLinkedId);
    /**
     * `"published"`, deleting nothing, when the draft turned out to be published (e.g. from another tab since this view
     * last rendered): it has to be deleted as a Rulebook.
     */
    const deleteDraft = useCallback(() => {
        if (getRulebookDraft({ id: draftId })?.rulebook_id !== undefined) {
            return "published";
        }
        return deleteRulebookDraft({ id: draftId }) ? "deleted" : "draftFailed";
    }, [draftId]);
    /**
     * `"serverFailed"` keeps the draft and its link. `"draftFailed"` means the Rulebook is gone but the draft couldn't be
     * removed locally; retrying is safe, since deleting a Rulebook that is already gone counts as deleted.
     */
    const deleteRulebook = useCallback(async () => {
        // The account deleting: a switch while the request runs must not remove the next account's draft.
        const ownerId = getRulebookDraftOwner();
        const linkedId = getRulebookDraft({ id: draftId })?.rulebook_id;
        try {
            if (linkedId !== undefined) {
                await client.deleteRulebook({ rulebookId: linkedId });
            }
        }
        catch {
            return "serverFailed";
        }
        if (getRulebookDraftOwner() !== ownerId) {
            // The Rulebook is gone; the old account's draft is left, and its next load unlinks it (404).
            return "draftFailed";
        }
        return deleteRulebookDraft({ id: draftId }) ? "deleted" : "draftFailed";
    }, [client, draftId]);
    return { isPublished: rulebookId !== undefined, deleteDraft, deleteRulebook };
}
